import type {OfferTemplate, OfferTemplateBindingDefinitions} from "./types/offerTemplates.types";
import {ZodError} from "zod";
import type {z} from "zod";
import type {OfferTemplateDto} from "@project/sdk/sales/zod";
import {CatalogItemFacts} from "@project/sdk/sales/zod";

type Location = (string | number)[];
function invalid(path: Location, message: string): never {
  throw new ZodError([{code: "custom", path, message}]);
}
// Sales preview/content objects are closed; shared Currency/Money/Notes are not.
function closed<DocumentFields extends Record<string, unknown>>(
  raw: DocumentFields | null | undefined,
  parsed: DocumentFields,
  path: Location,
  fields: readonly string[] = Object.keys(parsed),
): void {
  if (raw !== null && typeof raw === "object") {
    for (const key of Object.keys(raw)) {
      if (!fields.includes(key)) invalid([...path, key], "Undeclared Template field");
    }
  }
}
export function assertOfferTemplateBindingDefinitions(
  definitions: OfferTemplateBindingDefinitions,
  raw: OfferTemplateBindingDefinitions,
): void {
  definitions.forEach((definition, index) => closed(raw[index], definition, [index]));
  unique(
    definitions.map((definition) => definition.key),
    [],
    "key",
  );
}
function availability<DocumentFields extends Record<string, unknown>>(
  object: DocumentFields,
  state: string,
  key: string,
  path: Location,
): void {
  const present = Object.hasOwn(object, key);
  if (state === "AVAILABLE" ? !present || object[key] === undefined : present)
    invalid([...path, key], "Source value must match its declared availability");
}
function unique(values: string[], path: Location, key: string): void {
  const seen = new Set<string>();
  values.forEach((value, index) => {
    if (seen.has(value)) invalid([...path, index, key], "Duplicate identity in owning scope");
    seen.add(value);
  });
}

// Template preview has no commercial lifecycle or Expedient-dependent finances.
// It admits exact parsed facts, never acquires/recalculates sources or rewrites DTOs.
export function assertOfferTemplatePreview(
  template: OfferTemplate,
  raw: z.input<typeof OfferTemplateDto>,
): void {
  closed(raw, template, []);
  const contentRaw = raw.content;
  closed(contentRaw, template.content, ["content"]);
  const richText = (
    text: OfferTemplate["content"]["header"],
    original: OfferTemplate["content"]["header"] | undefined,
    path: Location,
  ) => {
    closed(original, text, path);
    unique(
      text.variables.map((item) => item.name),
      [...path, "variables"],
      "name",
    );
    text.variables.forEach((item, index) => {
      const variableRaw = original?.variables[index];
      const location = [...path, "variables", index];
      closed(variableRaw, item, location);
      if (item.value !== null) closed(variableRaw?.value, item.value, [...location, "value"]);
    });
  };
  richText(template.content.header, contentRaw.header, ["content", "header"]);
  richText(template.content.footer, contentRaw.footer, ["content", "footer"]);
  unique(
    template.content.chapters.map((item) => item.chapterId.toLowerCase()),
    ["content", "chapters"],
    "chapterId",
  );
  template.content.chapters.forEach((chapter, index) => {
    const original = contentRaw.chapters[index];
    const location = ["content", "chapters", index];
    closed(original, chapter, location);
    if (typeof original?.applyCommission !== "boolean")
      invalid([...location, "applyCommission"], "Explicit commission applicability is required");
    unique(
      chapter.blocks.map((block) => block.blockId.toLowerCase()),
      [...location, "blocks"],
      "blockId",
    );
    chapter.blocks.forEach((block, blockIndex) => {
      const blockRaw = original?.blocks[blockIndex];
      const path = [...location, "blocks", blockIndex];
      closed(blockRaw, block, path);
      if (block.blockType === "RICH_TEXT")
        richText(
          block.content,
          blockRaw?.blockType === "RICH_TEXT" ? blockRaw.content : undefined,
          [...path, "content"],
        );
      else
        block.selectedSupplierOffers.forEach((selection, selectionIndex) =>
          closed(
            blockRaw?.blockType === "CATALOG_ITEM"
              ? blockRaw.selectedSupplierOffers[selectionIndex]
              : undefined,
            selection,
            [...path, "selectedSupplierOffers", selectionIndex],
          ),
        );
    });
  });
  const evaluation = template.evaluation;
  const evaluationRaw = raw.evaluation;
  closed(evaluationRaw, evaluation, ["evaluation"]);

  const rates = evaluation.exchangeRates;
  closed(
    evaluationRaw?.exchangeRates,
    rates,
    ["evaluation", "exchangeRates"],
    rates.status === "NOT_USED"
      ? ["status"]
      : rates.status === "AVAILABLE"
        ? ["status", "provider", "publicationDate"]
        : rates.reason === "MISSING_PUBLICATION"
          ? ["status", "provider", "reason", "requiredPublicationDate"]
          : ["status", "provider", "reason", "publicationDate", "requiredPublicationDate"],
  );
  evaluation.variables.forEach((variable, index) => {
    const path = ["evaluation", "variables", index];
    closed(evaluationRaw?.variables[index], variable, path, [
      "scope",
      "chapterId",
      "blockId",
      "name",
      "value",
    ] satisfies readonly (keyof typeof variable)[]);
    if (variable.scope === "BLOCK" && (!variable.chapterId || !variable.blockId))
      invalid(path, "Block variable requires its actual Chapter and Block");
  });
  evaluation.issues.forEach((issue, index) => {
    const path = ["evaluation", "issues", index];
    closed(evaluationRaw?.issues[index], issue, path, [
      "code",
      "scope",
      "chapterId",
      "blockId",
      "leafCatalogItemId",
      "variableName",
    ] satisfies readonly (keyof typeof issue)[]);
    if (
      issue.code === "VARIABLE_UNRESOLVED" &&
      (!issue.scope || issue.variableName === null || issue.variableName === undefined)
    )
      invalid(path, "Unresolved variable requires scope and name");
    const located =
      issue.scope === "BLOCK" ||
      [
        "SUPPLIER_NOT_SELECTABLE",
        "TERMS_UNPRICED",
        "CURRENCY_MISMATCH",
        "LEAF_COVERAGE_INVALID",
        "QUANTITY_UNRESOLVED",
      ].includes(issue.code);
    if (located && (!issue.chapterId || !issue.blockId))
      invalid(path, "Issue requires its actual Chapter and Block");
    if (
      ["SUPPLIER_NOT_SELECTABLE", "TERMS_UNPRICED", "CURRENCY_MISMATCH"].includes(issue.code) &&
      !issue.leafCatalogItemId
    )
      invalid(path, "Source issue requires its actual leaf");
  });
  evaluation.catalogBlocks.forEach((block, index) => {
    const path = ["evaluation", "catalogBlocks", index];
    const original = evaluationRaw?.catalogBlocks[index];
    closed(original, block, path, [
      "chapterId",
      "blockId",
      "soldCatalogItem",
      "selectedSupplierOffers",
      "quantity",
      "priceProtected",
      "costAvailability",
      "unitSupplierCost",
      "supplierCost",
    ] satisfies readonly (keyof typeof block)[]);
    if (block.soldCatalogItem)
      closed(
        original?.soldCatalogItem,
        block.soldCatalogItem,
        [...path, "soldCatalogItem"],
        CatalogItemFacts.keyof().options,
      );
    availability(block, block.costAvailability, "unitSupplierCost", path);
    availability(block, block.costAvailability, "supplierCost", path);
    block.selectedSupplierOffers.forEach((selection, selectionIndex) => {
      const location = [...path, "selectedSupplierOffers", selectionIndex];
      closed(original?.selectedSupplierOffers[selectionIndex], selection, location, [
        "supplierId",
        "catalogItemId",
        "supplierName",
        "priceProtected",
        "priceAvailability",
        "conversionAvailability",
        "conversion",
        "unitPrice",
        "minimumOrderQuantity",
        "orderQuantityMultiple",
        "quantityPerSoldUnit",
        "effectiveQuantity",
      ] satisfies readonly (keyof typeof selection)[]);
      availability(selection, selection.priceAvailability, "unitPrice", location);

      availability(selection, selection.conversionAvailability, "conversion", location);
      if (
        (selection.priceAvailability === "RESTRICTED") !==
        (selection.conversionAvailability === "RESTRICTED")
      )
        invalid(location, "Restricted native price and conversion must agree");
      if (
        selection.priceAvailability === "UNAVAILABLE" &&
        selection.conversionAvailability !== "UNAVAILABLE"
      )
        invalid(location, "Unavailable native source cannot disclose a conversion");
      const conversion = selection.conversion;
      if (conversion) {
        closed(
          original?.selectedSupplierOffers[selectionIndex]?.conversion,
          conversion,
          [...location, "conversion"],
          ["sourceCurrency", "targetCurrency", "factor"],
        );
        if (
          selection.priceAvailability !== "AVAILABLE" ||
          conversion.sourceCurrency !== selection.unitPrice?.currency.code ||
          conversion.targetCurrency !== template.currency.code ||
          conversion.sourceCurrency === conversion.targetCurrency ||
          rates.status !== "AVAILABLE"
        )
          invalid(
            [...location, "conversion"],
            "Conversion must match native and quotation Currency and available evidence",
          );
      }
      if (selection.priceAvailability === "AVAILABLE" && selection.unitPrice) {
        const same = selection.unitPrice.currency.code === template.currency.code;
        if (same !== (selection.conversionAvailability === "NOT_REQUIRED"))
          invalid(location, "Only same-Currency native prices require no conversion");
        if (!same && selection.conversionAvailability === "UNAVAILABLE") {
          if (block.costAvailability === "AVAILABLE")
            invalid(path, "Unavailable conversion cannot disclose converted block cost");
          if (
            ["EUR", "USD", "GBP", "NOK"].includes(selection.unitPrice.currency.code) &&
            ["EUR", "USD", "GBP", "NOK"].includes(template.currency.code) &&
            rates.status !== "UNAVAILABLE"
          )
            invalid(
              location,
              "Unavailable supported conversion requires unavailable publication evidence",
            );
        }
      }
    });
    if (
      block.selectedSupplierOffers.some(
        (selection) => selection.priceAvailability === "RESTRICTED",
      ) &&
      !["RESTRICTED", "UNAVAILABLE"].includes(block.costAvailability)
    )
      invalid([...path, "costAvailability"], "Restricted input must protect block cost");
  });
}
